import { useQueryClient } from "@tanstack/react-query";
import { Download, FileArchive, FileJson2, FileSpreadsheet, Plus, X } from "lucide-react";
import { type FormEvent, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { ErrorState, LoadingState } from "../components/StateViews";
import { useFilters } from "../contexts/FiltersContext";
import { appConfig } from "../lib/config";
import { createManualMetric } from "../lib/data";
import { civilDateValue, rangeForCivilDates } from "../lib/dates";
import { buildExportDatasets, exportAll, exportDataset } from "../lib/export";
import { useEstufaData } from "../lib/queries";

const labels: Record<string, { label: string; description: string }> = {
  summary_metrics: {
    label: "Resumo de métricas",
    description: "KPIs e denominadores do recorte atual.",
  },
  timeseries: { label: "Série temporal", description: "Buckets por dia, semana ou mês." },
  funnel: { label: "Funil", description: "Etapas e conversão entre etapas." },
  installations: { label: "Instalações", description: "Estado e marcos de cada tenant." },
  raw_events: { label: "Eventos brutos", description: "Telemetria sanitizada do recorte." },
  metric_snapshots: {
    label: "Métricas externas",
    description: "Impressões/listing views e dados de painéis importados ou registrados.",
  },
  subscriptions: { label: "Assinaturas", description: "Planos, status e MRR normalizado." },
  revenue: { label: "Receita", description: "Entradas reconhecidas no período." },
  churn: { label: "Churn", description: "Assinaturas canceladas no recorte." },
  cohorts: { label: "Cohorts", description: "Retenção observável por cohort e dia." },
  feature_usage: { label: "Uso de features", description: "Contagem de eventos `feature_used`." },
  incidents: { label: "Incidentes", description: "Falhas operacionais e confiabilidade." },
  support_tickets: { label: "Suporte", description: "Tickets, severidade e tempos." },
  reviews: { label: "Reviews", description: "Ratings e tags autorizados." },
  releases: { label: "Releases", description: "Versões e hipóteses de lançamento." },
  experiments: { label: "Experimentos", description: "Testes e verdicts." },
  decisions: { label: "Decisões", description: "Decision Log da fábrica." },
  lessons: { label: "Aprendizados", description: "Memória cumulativa de acertos e erros." },
  opportunity_scores: {
    label: "Opportunity Scores",
    description: "Subscores preservados separadamente.",
  },
  research_dataset: {
    label: "Research dataset",
    description: "Observações de mercado autorizadas.",
  },
};

export function ReportsPage() {
  const query = useEstufaData();
  const { filter } = useFilters();
  const [manualOpen, setManualOpen] = useState(false);
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState />;
  const data = query.data;
  const datasets = buildExportDatasets(data, filter);
  return (
    <>
      <PageHeader
        eyebrow="Portabilidade total"
        title="Relatórios & Exportações"
        description="Baixe o recorte atual por Seed, plataforma e período. O conhecimento não fica preso na interface."
        action={
          <div className="button-row">
            <button type="button" className="secondary-button" onClick={() => setManualOpen(true)}>
              <Plus size={17} />
              Métrica manual
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={() => void exportAll(data, filter)}
            >
              <FileArchive size={17} />
              Exportar tudo
            </button>
          </div>
        }
      />
      <div className="export-grid">
        {Object.entries(datasets)
          .filter(([key]) => !["platforms", "seeds", "deployments"].includes(key))
          .map(([key, rows]) => {
            const info = labels[key] ?? { label: key, description: "Dataset exportável." };
            return (
              <article className="export-card" key={key}>
                <div className="export-icon">
                  <Download size={20} />
                </div>
                <div>
                  <h3>{info.label}</h3>
                  <p>{info.description}</p>
                  <span>{rows.length} registros disponíveis</span>
                </div>
                <div className="export-actions">
                  <button type="button" onClick={() => exportDataset(key, rows, "csv")}>
                    <FileSpreadsheet size={16} />
                    CSV
                  </button>
                  <button type="button" onClick={() => exportDataset(key, rows, "json")}>
                    <FileJson2 size={16} />
                    JSON
                  </button>
                </div>
              </article>
            );
          })}
      </div>
      <div className="notice-card">
        <FileArchive size={20} />
        <div>
          <strong>O ZIP respeita o filtro atual</strong>
          <p>
            Inclui manifest, CSV e JSON de todos os datasets autorizados. Secrets, capabilities e
            PII não fazem parte do modelo de exportação.
          </p>
        </div>
      </div>
      {manualOpen ? (
        <ManualMetricModal
          deployments={data.deployments}
          disabled={appConfig.demoMode}
          onClose={() => setManualOpen(false)}
        />
      ) : null}
    </>
  );
}

function ManualMetricModal({
  deployments,
  disabled,
  onClose,
}: {
  deployments: { id: string; name: string }[];
  disabled: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled) return;
    setSaving(true);
    setError(null);
    const form = new FormData(event.currentTarget);
    try {
      const date = String(form.get("date"));
      const range = rangeForCivilDates(date, date, appConfig.timezone);
      await createManualMetric({
        deploymentId: String(form.get("deploymentId")),
        metricName: String(form.get("metricName")),
        value: Number(form.get("value")),
        periodStart: range.from.toISOString(),
        periodEnd: range.to.toISOString(),
        granularity: "day",
      });
      await queryClient.invalidateQueries({ queryKey: ["estufa-data"] });
      onClose();
    } catch {
      setError("Não foi possível registrar a métrica.");
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <div className="modal-header">
          <h2>Métrica externa/manual</h2>
          <button type="button" className="icon-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <p className="inline-note">
          Use para dados que o marketplace mostra em painel, mas não fornece automaticamente. A
          origem ficará marcada como `manual_entry`.
        </p>
        <form className="form-stack" onSubmit={(event) => void submit(event)}>
          <label>
            Deployment
            <select name="deploymentId" required>
              {deployments.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Métrica
            <select name="metricName" defaultValue="impressions">
              <option value="impressions">impressions</option>
              <option value="listing_views">listing_views</option>
              <option value="marketplace_position">marketplace_position</option>
              <option value="listing_clicks">listing_clicks</option>
            </select>
          </label>
          <div className="form-grid">
            <label>
              Valor
              <input name="value" type="number" step="any" required />
            </label>
            <label>
              Data
              <input
                name="date"
                type="date"
                required
                defaultValue={civilDateValue(new Date(), appConfig.timezone)}
              />
            </label>
          </div>
          {error ? <p className="form-error">{error}</p> : null}
          <button type="submit" className="primary-button" disabled={saving || disabled}>
            {disabled ? "Bloqueado no demo" : saving ? "Salvando…" : "Salvar métrica"}
          </button>
        </form>
      </div>
    </div>
  );
}
